import { useMemo, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { ApiResponse } from "@entrilia/shared/api/types";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type { ReportGroup, ReportInfo } from "../../../../../../api/types/svTypes.ts";
import type { SideMenuFilter } from "../components/ReportsSideMenu.tsx";
import type { ReportInfoExt } from "../types/reportTypes.ts";
import {
  getActiveSideMenuFilter,
  getSideMenuFilterLabel,
  getSideMenuItemFields,
  isInSideMenuFilter,
} from "./sideMenuItems.ts";
import useFavoriteReports from "./useFavoriteReports.ts";

interface UseReportsWithSideMenuParams {
  reports: ReportInfo[];
  reportGroups: Record<string, ReportGroup[]>;
  clientCode: string;
  getFavoriteReports: (clientCode: string) => Promise<ApiResponse<string[]>>;
  toggleFavoriteReport: (clientCode: string, reportId: string) => Promise<ApiResponse<string[]>>;
}

type SideMenuFilterType = "all" | "favorites" | "group";

interface UseReportsWithSideMenuResult {
  filteredReports: ReportInfoExt[];
  groups: ReportGroup[];
  reportCountsByGroup: Record<string, number>;
  sideMenuFilter: SideMenuFilter;
  sideMenuFilterType: SideMenuFilterType;
  selectedFilterLabel: string;
  setSideMenuFilter: (filter: SideMenuFilter) => void;
  toggleFavorite: (reportId: string) => void;
}

const useReportsWithSideMenu = ({
  reports,
  reportGroups,
  clientCode,
  getFavoriteReports,
  toggleFavoriteReport,
}: UseReportsWithSideMenuParams): UseReportsWithSideMenuResult => {
  const { favoriteIds, toggleFavorite } = useFavoriteReports({ clientCode, getFavoriteReports, toggleFavoriteReport });
  const [sideMenuFilter, setSideMenuFilter] = useState<SideMenuFilter>("all");

  const allReports = useMemo(() => {
    return reports
      .filter((r) => r.authorization.access !== ReportAccessType.NoAccess)
      .map((r): ReportInfoExt => ({
        ...r,
        ...getSideMenuItemFields(
          defined(reportGroups[clientCode]).find((rg) => rg.id === r.groupId),
          favoriteIds.has(r.reportId)
        ),
      }));
  }, [reports, reportGroups, clientCode, favoriteIds]);

  const groups = useMemo(() => {
    const clientGroups = reportGroups[clientCode] ?? [];
    return [...clientGroups].sort((a, b) => a.order - b.order);
  }, [reportGroups, clientCode]);

  const activeFilter = getActiveSideMenuFilter(sideMenuFilter, groups);
  const sideMenuFilterType: SideMenuFilterType =
    activeFilter === "all" || activeFilter === "favorites" ? activeFilter : "group";

  const filteredReports = useMemo(
    () => allReports.filter((report) => isInSideMenuFilter(activeFilter, report)),
    [allReports, activeFilter]
  );

  const selectedFilterLabel = getSideMenuFilterLabel(activeFilter, groups, "All Reports");

  const reportCountsByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of allReports) {
      counts[r.groupId] = (counts[r.groupId] ?? 0) + 1;
    }
    return counts;
  }, [allReports]);

  return {
    filteredReports,
    groups,
    reportCountsByGroup,
    sideMenuFilter: activeFilter,
    sideMenuFilterType,
    selectedFilterLabel,
    setSideMenuFilter,
    toggleFavorite,
  };
};

export default useReportsWithSideMenu;
